import { startOfWeek } from 'date-fns';
import { AnyObject } from 'final-form';
import { clone, pathOr, Path } from 'ramda';
import { v4 } from 'uuid';

import { ShiftBreak, ShiftTemplateType } from '../../graphql/components';
import { ONE_HOUR_IN_SECONDS, ONE_MINUTE_IN_SECONDS } from '../../utils/time';
import { composeValidators } from '../../utils/validations/composeValidators';
import { inRange } from '../../utils/validations/inRange';
import { required } from '../../utils/validations/required';
import { requiredWithOtherFields } from '../../utils/validations/requiredWithOtherFields';
import { TArrayValidator, TValidator } from '../../utils/validations/types';
import { DayTemplatePartial, ShiftTemplatesFormValues } from './types';

export const isDayTemplateEmpty = (dayTemplate: DayTemplatePartial) => {
  if (!dayTemplate) {
    return true;
  }

  const breaks = dayTemplate.breaks?.filter(
    ({ duration, entitlementAfter, type }) =>
      duration !== undefined || entitlementAfter !== undefined || type !== undefined
  );

  const activities = dayTemplate.scheduleActivitySettings?.filter(
    ({ minimumPerDay, maximumPerDay }) => minimumPerDay || maximumPerDay
  );

  if (
    activities.length ||
    breaks.length ||
    dayTemplate.shiftRange?.startTime !== undefined ||
    dayTemplate.shiftDuration !== undefined ||
    dayTemplate.shiftsMinGap !== undefined ||
    dayTemplate.breaksMinGap !== undefined
  ) {
    return false;
  }

  return true;
};

export const cleanUninitializedShiftData = (data: ShiftTemplatesFormValues) => {
  const result = clone(data);
  const daysSettingToRemove: number[] = [];

  // This is only helper value and needs to be removed before sending data to API
  delete result.cloneShiftTemplateId;

  data.dayTemplates.forEach((dayTemplate, index) => {
    const breaks = dayTemplate.breaks?.filter(
      ({ duration, entitlementAfter, type }) =>
        duration !== undefined && entitlementAfter !== undefined && type !== undefined
    );
    const activitySettings = dayTemplate.scheduleActivitySettings.filter(({ type }) => type !== undefined);

    result.dayTemplates[index].breaks = breaks;
    result.dayTemplates[index].scheduleActivitySettings = activitySettings;

    if (data.type === ShiftTemplateType.EvenOdd && isDayTemplateEmpty(dayTemplate)) {
      daysSettingToRemove.push(index);
    }
  });

  if (daysSettingToRemove.length) {
    result.dayTemplates = result.dayTemplates.filter((_, index) => !daysSettingToRemove.includes(index));
  }

  return result;
};

export const getFieldPrefixPath: (fieldPrefix: string) => Path = (fieldPrefix) =>
  fieldPrefix
    .split('.')
    .map((partialPrefix) => {
      const containIndex = partialPrefix.match(/[(\d+)]/);
      if (containIndex) {
        const partialPrefixNameEnd = partialPrefix.indexOf('[');
        return [partialPrefix.slice(0, partialPrefixNameEnd), Number.parseInt(containIndex[0], 10)];
      }

      return partialPrefix;
    })
    .flatMap((item) => item);

export const initializeBreakValue: (id?: string) => Partial<ShiftBreak> = (id) => ({
  duration: undefined,
  entitlementAfter: undefined,
  id: id ?? v4(),
  type: undefined,
});

export const requiredWhenAtLeastTwoBreaksAreDefined: (pathToBreaks: Path) => TValidator =
  (pathToBreaks) => (value, allValues) => {
    if (!pathToBreaks) {
      return undefined;
    }

    const breaks: ShiftBreak[] = pathOr([], pathToBreaks, allValues);

    if (!Array.isArray(breaks) || breaks.length < 2) {
      return undefined;
    }

    let countOfNotEmptyBreaks = 0;

    breaks.forEach(({ type, duration, entitlementAfter }) => {
      if (countOfNotEmptyBreaks === 2) {
        // do not check other breaks if there are already two fully-filled ones
        return;
      }
      if (type !== undefined || duration !== undefined || entitlementAfter !== undefined) {
        countOfNotEmptyBreaks += 1;
      }
    });

    if (countOfNotEmptyBreaks < 2) {
      return undefined;
    }

    return required(value);
  };

export const validateDayTemplates: TArrayValidator = (
  value: DayTemplatePartial[],
  allValues: ShiftTemplatesFormValues
) => {
  const withinOneDayValidator = inRange(15 * ONE_MINUTE_IN_SECONDS, 24 * ONE_HOUR_IN_SECONDS); // With 15 minutes steps

  const allDaysErrors: Array<Record<string, string | undefined>> = [];

  const valuesEmpty = value.map((dayTemplate) => isDayTemplateEmpty(dayTemplate));
  const isRequired = value.length === 1 || valuesEmpty.every((emptyValue) => emptyValue);

  value.forEach((dayTemplate: DayTemplatePartial, dayTemplateIndex) => {
    const errors = {} as AnyObject;
    const isRequiredActive = isRequired || !valuesEmpty[dayTemplateIndex];

    let sumOfActivitiesMinimums = 0;

    errors.shiftRange = {
      startTime: isRequiredActive ? required(dayTemplate.shiftRange?.startTime) : undefined,
    };

    dayTemplate.scheduleActivitySettings.forEach(({ minimumPerDay }) => {
      sumOfActivitiesMinimums += minimumPerDay ?? 0;
    });

    const sumOfMinsBiggerThanShiftDurationError =
      dayTemplate.shiftDuration !== undefined && sumOfActivitiesMinimums > dayTemplate.shiftDuration
        ? 'activitySumOfMinsCantBeHigherThanShiftDuration'
        : undefined;

    errors.shiftDuration = isRequiredActive
      ? composeValidators(required, withinOneDayValidator)(dayTemplate.shiftDuration)
      : withinOneDayValidator(dayTemplate.shiftDuration);

    errors.shiftsMinGap = isRequiredActive
      ? composeValidators(required, withinOneDayValidator)(dayTemplate.shiftsMinGap)
      : withinOneDayValidator(dayTemplate.shiftsMinGap);

    errors.breaks = dayTemplate.breaks.map(({ duration, entitlementAfter, type }, shiftBreakIndex) => {
      const fieldPrefixPath = ['dayTemplates', dayTemplateIndex, 'breaks', shiftBreakIndex];

      const entitlementAfterBiggerThanShiftDurationError =
        entitlementAfter !== undefined &&
        dayTemplate.shiftDuration !== undefined &&
        entitlementAfter > dayTemplate.shiftDuration
          ? 'breakEntitlementAfterHigherThanShiftDuration'
          : undefined;

      return {
        duration: composeValidators(
          requiredWithOtherFields([
            [...fieldPrefixPath, 'entitlementAfter'],
            [...fieldPrefixPath, 'type'],
          ]),
          withinOneDayValidator
        )(duration, allValues),
        entitlementAfter:
          requiredWithOtherFields([
            [...fieldPrefixPath, 'duration'],
            [...fieldPrefixPath, 'type'],
          ])(entitlementAfter, allValues) ?? entitlementAfterBiggerThanShiftDurationError,
        type: requiredWithOtherFields([
          [...fieldPrefixPath, 'duration'],
          [...fieldPrefixPath, 'entitlementAfter'],
        ])(type, allValues),
      };
    });

    errors.breaksMinGap = isRequiredActive
      ? composeValidators(requiredWhenAtLeastTwoBreaksAreDefined(['breaks']), withinOneDayValidator)(
          dayTemplate.breaksMinGap,
          dayTemplate
        )
      : undefined;

    errors.scheduleActivitySettings = dayTemplate.scheduleActivitySettings.map(({ minimumPerDay, maximumPerDay }) => {
      const isMinBiggerThanMax =
        minimumPerDay !== undefined && maximumPerDay !== undefined && minimumPerDay > maximumPerDay;
      const maxBiggerThanShiftDuration =
        maximumPerDay !== undefined &&
        dayTemplate.shiftDuration !== undefined &&
        maximumPerDay > dayTemplate.shiftDuration;

      const minErrors = [];
      const maxErrors = [];

      if (sumOfMinsBiggerThanShiftDurationError) {
        minErrors.push(sumOfMinsBiggerThanShiftDurationError);
      }

      if (isMinBiggerThanMax) {
        minErrors.push('activityMaxCantBeLowerThanMin');
        maxErrors.push('activityMaxCantBeLowerThanMin');
      }

      if (maxBiggerThanShiftDuration) {
        maxErrors.push('activityMaxCantBeHigherThanShiftDuration');
      }

      const minRequired = required(minimumPerDay);
      if (minRequired) {
        minErrors.push(minRequired);
      }

      const maxRequired = required(maximumPerDay);
      if (maxRequired) {
        maxErrors.push(maxRequired);
      }

      return {
        minimumPerDay: minErrors.length ? minErrors : undefined,
        maximumPerDay: maxErrors.length ? maxErrors : undefined,
      };
    });

    // do cleanups on objects and arrays fields
    if (errors.shiftRange.startTime === undefined) {
      errors.shiftRange = undefined;
    }

    if (
      errors.breaks.every((breakError: Record<string, string | undefined>) =>
        Object.values(breakError).every((breakValue) => breakValue === undefined)
      )
    ) {
      errors.breaks = undefined;
    }

    if (
      errors.scheduleActivitySettings.every((scheduleActivityError: Record<string, string | string[] | undefined>) =>
        Object.values(scheduleActivityError).every((scheduleActivityValue) => scheduleActivityValue === undefined)
      )
    ) {
      errors.scheduleActivitySettings = undefined;
    }

    allDaysErrors.push(errors);
  });

  if (allDaysErrors.some((dayErrors) => Object.values(dayErrors).some((error) => error !== undefined))) {
    return allDaysErrors;
  }

  return undefined;
};

export const duplicateTemplateSettings: (
  oldTemplate: DayTemplatePartial,
  newTemplate: DayTemplatePartial
) => DayTemplatePartial = (oldTemplate, newTemplate) => {
  const template = clone(newTemplate);

  template.dayIndex = oldTemplate.dayIndex;
  template.id = oldTemplate.id;

  template.breaks = template.breaks?.map((shiftBreak) => ({ ...shiftBreak, id: v4() }));
  template.scheduleActivitySettings = template.scheduleActivitySettings?.map((activity) => ({ ...activity, id: v4() }));

  return template;
};

/**
 * return dayIndex for start day of even/odd week
 * @param isEvenWeek boolean even / odd week type
 * @returns value in range of [0,13] where:
 * - &nbsp;&nbsp;__0__&nbsp; stands for monday in even week
 * - &nbsp;&nbsp;__6__&nbsp; stands for sunday in even week
 * - &nbsp;&nbsp;__7__&nbsp; stands for monday in odd week
 * - __13__&nbsp; stands for sunday in odd week
 * - and so on...
 */
export const getShiftTemplateIndexForStartOfEvenOddWeek = (localeObj?: Locale, isEvenWeek = true) => {
  const dayOfWeek = startOfWeek(new Date(), { locale: localeObj }).getDay();
  const oddWeekModulo = isEvenWeek ? 0 : 7;

  return oddWeekModulo + (dayOfWeek === 0 ? 6 : dayOfWeek - 1);
};
